import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { canonicalLink, canonicalUrl, geoMeta, SITE_URL } from "@/lib/seo";
import { businessHeadScripts } from "@/lib/businessJsonLd";

const TITLE = "Privacy Policy | Texas Made Shade & Covers";
const DESCRIPTION =
  "How Texas Made Shade & Covers collects, uses and protects the name, phone, email, address and project photos you share with us, plus your choices and how to contact us about your data.";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    links: [canonicalLink("/privacy-policy")],
    meta: [
      ...geoMeta(),
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Privacy Policy — Texas Made Shade & Covers" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: canonicalUrl("/privacy-policy") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [
      ...businessHeadScripts(),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Privacy Policy", item: canonicalUrl("/privacy-policy") },
          ],
        }),
      },
    ],
  }),
  component: PrivacyPolicyPage,
});

const LAST_UPDATED = "September 2026";

type Section = { heading: string; body: string[]; list?: string[] };

const SECTIONS: Section[] = [
  {
    heading: "Who we are",
    body: [
      "Texas Made Shade & Covers (\"we\", \"us\") is a custom fabrication and installation company based at 959 East Kingsbury Street, Seguin, TX 78155. This policy explains what information we collect through texasmadeshade.com, why we collect it, and the choices you have.",
    ],
  },
  {
    heading: "Information we collect",
    body: [
      "We collect information you give us directly, and a small amount of technical information about how the site is used:",
    ],
    list: [
      "Contact and project details you submit through our quote and contact forms — typically your name, phone number, email address, city or project address, and any message or measurements you include.",
      "Photos and files you upload with a quote request, so we can see the opening, structure or project you're asking about.",
      "Order and shop information when you buy hardware or supplies from us — billing details, shipping address, and what you ordered. Payments are processed by our payment processor; we do not store full card numbers.",
      "Basic usage data (pages viewed, approximate location derived from IP, device type) collected through Google Analytics to understand which pages help customers.",
    ],
  },
  {
    heading: "How we use it",
    body: ["We use the information above to:"],
    list: [
      "Respond to your quote request, question or order — including calling, texting or emailing you back.",
      "Measure, fabricate, deliver and install your project, and to coordinate scheduling.",
      "Provide service, warranty and follow-up on work we've done for you.",
      "Send service-related communications, and — only if you opt in — occasional updates from the shop. Every marketing email includes an unsubscribe link, and we honor opt-outs promptly.",
      "Keep basic business records (quotes, orders, job history) and improve the website.",
    ],
  },
  {
    heading: "What we don't do",
    body: [
      "We do not sell or rent your personal information. We do not share it with advertisers. We only share it when it's needed to run the business — for example with our website and email service providers, our payment processor, or delivery partners for shop orders — or when the law requires it.",
    ],
  },
  {
    heading: "How long we keep it",
    body: [
      "Quote requests, job records and order history are kept for as long as needed to serve your project and meet ordinary Texas business recordkeeping and tax obligations. Uploaded photos are kept with your project record. You can ask us to delete personal information we hold about you (see below), subject to the records we're required to keep.",
    ],
  },
  {
    heading: "Cookies and analytics",
    body: [
      "This site uses a small number of cookies and similar technologies: Google Analytics (with a Google Ads tracking tag) to measure which pages and campaigns work, and local browser storage to keep the site functioning. Analytics data is used in aggregate — we use it to see traffic patterns, not to build profiles for other companies. You can block cookies in your browser and still use the site; if you'd like, you can also opt out of Google Analytics with Google's browser add-on.",
    ],
  },
  {
    heading: "Your choices and Texas privacy rights",
    body: [
      "You can contact us at any time to:",
    ],
    list: [
      "Ask what personal information we hold about you.",
      "Request a correction or deletion of your personal information.",
      "Opt out of marketing emails or texts (every email has an unsubscribe link; you can also just tell us).",
      "Ask us not to use your project photos in our gallery or social media. We always prefer to ask first.",
    ],
  },
  {
    heading: "Security",
    body: [
      "Form submissions and file uploads are transmitted over encrypted connections (HTTPS) and stored in an access-controlled database. Access is limited to the team members who need it to answer and serve your request. No method of transmission or storage is perfectly secure, but we take reasonable steps consistent with the size and nature of our business.",
    ],
  },
  {
    heading: "Children's privacy",
    body: [
      "Our website is intended for adults. We do not knowingly collect personal information from children under 13. If you believe a child has submitted information to us, contact us and we'll delete it.",
    ],
  },
  {
    heading: "Changes to this policy",
    body: [
      `We update this page when our practices change. The version above was last updated in ${LAST_UPDATED}. Material changes will be noted on this page.`,
    ],
  },
];

function PrivacyPolicyPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1} className="bg-cream">
        <section className="border-b-8 border-navy-deep bg-navy-deep py-14 text-white lg:py-20">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <p className="font-display text-xs uppercase tracking-[0.3em] text-red">
              Privacy Policy
            </p>
            <h1 className="mt-3 font-display text-4xl font-bold uppercase leading-tight md:text-5xl">
              Privacy Policy
            </h1>
            <p className="mt-4 max-w-3xl text-base text-white/85 md:text-lg">
              What we collect when you request a quote, place a shop order, or browse the site —
              and the choices you have. Plain language, no legalese for its own sake.
            </p>
            <p className="mt-3 text-xs uppercase tracking-widest text-white/70">
              Last updated: {LAST_UPDATED}
            </p>
          </div>
        </section>

        <section className="py-12 lg:py-16">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-3 lg:px-8">
            <div className="space-y-8 lg:col-span-2">
              {SECTIONS.map((s) => (
                <article key={s.heading} className="border-2 border-navy-deep bg-white p-6 shadow-card">
                  <h2 className="font-display text-2xl font-bold uppercase text-navy-deep">
                    {s.heading}
                  </h2>
                  {s.body.map((p) => (
                    <p key={p.slice(0, 40)} className="mt-3 text-base leading-relaxed text-navy-deep/80">
                      {p}
                    </p>
                  ))}
                  {s.list && (
                    <ul className="mt-3 space-y-2 text-base leading-relaxed text-navy-deep/80">
                      {s.list.map((item) => (
                        <li key={item.slice(0, 40)}>• {item}</li>
                      ))}
                    </ul>
                  )}
                </article>
              ))}
            </div>

            <div className="space-y-6">
              <div className="border-2 border-navy-deep bg-white p-5 shadow-card">
                <h2 className="font-display text-lg font-bold uppercase text-navy-deep">
                  Privacy questions?
                </h2>
                <p className="mt-2 text-sm text-navy-deep/75">
                  For anything about your data — access, correction, deletion, or opt-out — reach
                  the shop directly:
                </p>
                <ul className="mt-4 space-y-3 text-sm text-navy-deep/80">
                  <li className="flex items-center gap-2">
                    <Phone className="h-4 w-4 shrink-0 text-red" aria-hidden="true" focusable="false" />
                    <a href="tel:+18303909588" className="font-semibold underline decoration-red decoration-2 underline-offset-4 hover:text-red">
                      +1 (830) 390-9588
                    </a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Mail className="h-4 w-4 shrink-0 text-red" aria-hidden="true" focusable="false" />
                    <a href="mailto:office@texasmadeshade.com" className="break-all font-semibold underline decoration-red decoration-2 underline-offset-4 hover:text-red">
                      office@texasmadeshade.com
                    </a>
                  </li>
                  <li className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-red" aria-hidden="true" focusable="false" />
                    <span>959 East Kingsbury Street, Seguin, TX 78155</span>
                  </li>
                </ul>
              </div>

              <div className="border-2 border-navy-deep bg-white p-5 shadow-card">
                <h2 className="font-display text-lg font-bold uppercase text-navy-deep">
                  Related pages
                </h2>
                <ul className="mt-3 space-y-2 text-sm">
                  <li>
                    <Link to="/accessibility" className="font-medium text-navy-deep underline decoration-red decoration-2 underline-offset-4 hover:text-red">
                      Accessibility Statement
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact" className="font-medium text-navy-deep underline decoration-red decoration-2 underline-offset-4 hover:text-red">
                      Contact &amp; About Us
                    </Link>
                  </li>
                  <li>
                    <Link to="/local-faqs" className="font-medium text-navy-deep underline decoration-red decoration-2 underline-offset-4 hover:text-red">
                      Service Area FAQs
                    </Link>
                  </li>
                </ul>
                <Link
                  to="/estimate-quotes"
                  className="mt-5 inline-flex items-center gap-2 bg-red px-5 py-3 font-display text-sm uppercase tracking-wider text-white transition hover:bg-red-deep"
                >
                  Request a Quote <ArrowRight className="h-4 w-4" aria-hidden="true" focusable="false" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
